import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { useSnackbar } from "@kitbird/core";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  getMasterVendorDetail,
  getMasterVendors,
  IGetMasterVendorsData,
  postMasterVendors,
  putMasterVendor,
} from "../../../services/masterVendorService";
import useAuth from "../../../components/Authentication/useAuth";
import schema from "./schema";
import { byteToString } from "../../../utils/common";
import { getDocUrl } from "../../../services/bbOneGcsService";
import { getMasterBank } from "../../../services/masterBankService";
import { IOptionItem } from "../../../typings/common";
import { listCodeTypeAll } from "../../Transaction/CreateReceiptSubmission/constants";
import { getMasterDepartment, IGetMasterDepartmentData } from "../../../services/masterDepartmentService";

export interface FilterI {
  vendorStatus?: {
    label: string,
    value: string,
  },
  vendorApproval?: {
    label: string,
    value: string,
  },
  department?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  search?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

const useVendor = () => {
  const initPoolVendor = {
    codeType: {
      label: "",
      value: "",
    },
    area: {
      label: "",
      value: "",
    },
    pool: {
      label: "",
      value: "",
    },
    department: "",
  };
  const initVendorFile = {
    item_id: 0,
    file_name: "",
    file_base64: "",
    file_size: "",
    create_by: "",
  };

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));

  const { userInfo, userAccount } = useAuth();
  const snackbar = useSnackbar();
  const [params, setParams] = useState<FilterI>({
    pool: userAccount.userPools?.length === 1
      ? { label: userAccount.userPools[0].poolName, value: userAccount.userPools[0].poolId }
      : undefined,
    vendorApproval: { label: "Lihat semua", value: "" },
    vendorStatus: { label: "Lihat semua", value: "" },
    sortBy: "",
    sortType: "DSC",
  });

  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [isLoading, setLoading] = useState(false);
  const [modalFormVendor, setModalFormVendor] = useState(false);
  const [editValues, setEditValues] = useState<IGetMasterVendorsData>();
  const [files, setFiles] = useState<{
    id: number,
    trx_id: string,
    item_id: number,
    file_name: string,
    path_file: string,
    source_file: string,
    source_key: string,
    file_size: string,
    create_at: string,
    create_by: string
  }[]>([]);
  const [isViewFile, setViewFile] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [bankOpts, setBankOpts] = useState<IOptionItem[]>([]);
  const [metaBank, setMetaBank] = useState({
    page: 0,
    page_size: 0,
    total_items: 0,
    total_pages: 1,
  });
  const [allDepartment, setAllDepartment] = useState<IGetMasterDepartmentData[]>([]);

  const fetchBank = async () => {
    getMasterBank({ page_number: metaBank.page + 1, page_size: 10 })
      .then((res) => {
        setBankOpts((curr) => [...curr, ...res.list_mst_bank
          .map((item) => ({ label: item.bank_desc, value: item.bank_code }))]);
        setMetaBank(res.metadata);
      })
      .finally();
  };

  useEffect(() => {
    if (metaBank.page < metaBank.total_pages) fetchBank();
  }, [metaBank]);

  const initForm = {
    status_vendor: "N",
    vendor_name: "",
    vendor_address: "",
    type_vendor: "",
    bank_account: {
      label: "",
      value: "",
    },
    bank_account_number: "",
    bank_account_name: "",
    pool_department: [initPoolVendor],
    vendor_files: [initVendorFile],
  };

  const methods = useForm({
    defaultValues: initForm,
    resolver: joiResolver(schema),
  });
  const { getValues, reset, setError } = methods;

  const fetchList = async (pageParam: number) => {
    const sortBy = params.sortType === "DEFAULT" ? "" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    const hasRequiredParams = !!(params.pool && params.department);

    return hasRequiredParams
      ? getMasterVendors({
        department: params.department?.value,
        pool_id: params.pool?.value,
        employee_email: userInfo.email,
        page_number: pageParam,
        page_size: 10,
        search: params.search,
        status_approval: params.vendorApproval?.value,
        status_vendor: params.vendorStatus?.value,
        sort_by: sortBy,
        sort_type: sortType,
      }).then((res) => {
        if (res.metadata) setMeta(res.metadata);
        return res.list_mst_vendor;
      }).catch(() => [])
      : [];
  };

  const {
    data: masterVendorPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,
  } = useInfiniteQuery(
    ["masterVendorList", params],
    async ({ queryKey, pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const masterVendorList = useMemo(
    () => masterVendorPages.pages.flatMap((page) => page),
    [masterVendorPages, params],
  );

  const { data: dataDepartments } = useQuery({
    queryKey: "get_departments",
    queryFn: getMasterDepartment,
  });

  useEffect(() => {
    if (dataDepartments) setAllDepartment(dataDepartments.lstmstdepartment);
  }, [dataDepartments]);

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event: any) => {
      const val = {
        item_id: 0,
        file_name: file.name,
        file_base64: event.target.result.split(",")[1],
        file_size: byteToString(file.size as number, "B", false),
        create_by: userInfo.preferred_username,
      };

      const totalFileSize = file.size;
      const fileSizeLimit = 10 * 1024 * 1024;
      const isFileSizeAcceptable = fileSizeLimit >= totalFileSize;
      const fileType = val.file_name.split(".").pop() || "";
      const isFileTypeAcceptable = (/\b(png|jpe?g|pdf)\b/i).test(fileType);

      if (!isFileSizeAcceptable || !isFileTypeAcceptable) methods.setValue("vendor_files", [], { shouldDirty: true });
      if (isFileSizeAcceptable && isFileTypeAcceptable) {
        methods.setValue("vendor_files", [val], { shouldValidate: true });
      } else if (!isFileSizeAcceptable && isFileTypeAcceptable) {
        setError("vendor_files", { message: "Total ukuran file melebihi 1 MB" });
      } else if (isFileSizeAcceptable && !isFileTypeAcceptable) {
        setError("vendor_files", { message: "Jenis file tidak sesuai (bukan .png, .jpg, .jpeg, atau .pdf)" });
      } else if (!isFileSizeAcceptable && !isFileTypeAcceptable) {
        setError("vendor_files", { message: "Total ukuran file melebihi 1 MB dan jenis file tidak sesuai" });
      }
    };
  };

  const handlePost = async () => {
    setLoading(true);
    if (!editValues) {
      await postMasterVendors({
        vendor_name: getValues("vendor_name"),
        vendor_address: getValues("vendor_address"),
        type_vendor: getValues("type_vendor"),
        client_name: userAccount.client_name,
        bank_account: getValues("bank_account.value"), // bank_account
        bank_name: getValues("bank_account.label"), // bank_name
        bank_account_number: getValues("bank_account_number"),
        bank_account_name: getValues("bank_account_name"),
        code_type: userAccount.position_info[0].code_type,
        area_id: userAccount.position_info[0].area_id,
        pool_id: userAccount.position_info[0].pool_id,
        create_by: userInfo.preferred_username,
        lst_vendor_file: getValues("vendor_files"),
        lst_assignments: getValues("pool_department").map((item) => ({ pool_id: item.pool.value, department: item.department })),
      }).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          reset(initForm);
          setModalFormVendor(false);
          setEditValues(undefined);
          snackbar.show("Berhasil menambah data", "success");
        } else {
          snackbar.show(res.responseMessage || "Gagal menambah data", "error");
        }
      }).catch(() => {
        snackbar.show("Gagal menambah data", "error");
      }).finally(() => setLoading(false));
    } else {
      await putMasterVendor({
        vendor_code: editValues.vendor_code,
        status_vendor: getValues("status_vendor"),
        vendor_name: getValues("vendor_name"),
        vendor_address: getValues("vendor_address"),
        type_vendor: getValues("type_vendor"),
        client_name: userAccount.client_name,
        bank_account: getValues("bank_account.value"), // bank_account
        bank_name: getValues("bank_account.label"), // bank_name
        bank_account_number: getValues("bank_account_number"),
        bank_account_name: getValues("bank_account_name"),
        code_type: userAccount.position_info[0].code_type,
        area_id: userAccount.position_info[0].area_id,
        pool_id: userAccount.position_info[0].pool_id,
        create_by: userInfo.preferred_username,
        lst_assignments: getValues("pool_department").map((item) => ({ pool_id: item.pool.value, department: item.department })),
      }).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          reset(initForm);
          setModalFormVendor(false);
          setEditValues(undefined);
          snackbar.show("Berhasil mengubah data", "success");
        } else {
          snackbar.show(res.responseMessage || "Gagal mengubah data", "error");
        }
      }).catch(() => {
        snackbar.show("Gagal mengubah data", "error");
      }).finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (!modalFormVendor) {
      setEditValues(undefined);
      reset(initForm);
    }
  }, [modalFormVendor]);

  const fetchDetailVendor = () => {
    if (editValues) {
      getMasterVendorDetail(editValues.vendor_code).then((res) => {
        setFiles(res.vendor_files);
        reset({
          bank_account: {
            label: editValues.bank_name,
            value: editValues.bank_account,
          },
          bank_account_name: editValues.bank_account_name,
          bank_account_number: editValues.bank_account_number,
          pool_department: res.pool_department
            .map((item) => ({
              codeType: {
                label: listCodeTypeAll
                  .find((code) => code.code_type === item.code_type)?.code_type_desc
                  || item.code_type,
                value: item.code_type,
              },
              area: { label: item.area_desc.replace(/[^a-zA-Z\s]/g, ""), value: item.area_id },
              pool: { label: `${item.pool_name} • ${item.pool_id}`, value: item.pool_id },
              department: item.department,
            })),
          status_vendor: editValues.status_vendor,
          type_vendor: editValues.type_vendor,
          vendor_address: editValues.vendor_address,
          vendor_files: editValues.vendor_files,
          vendor_name: editValues.vendor_name,
        });
      });
    }
  };

  useEffect(() => {
    fetchDetailVendor();
  }, [editValues]);

  const handleDisplayDoc = async () => {
    setLoading(true);
    if (files.length > 0) {
      const docUrlString = await getDocUrl({
        file_name: files[0].file_name,
        path_file: files[0].path_file,
      });
      setDocUrl(docUrlString);
      setViewFile(true);
    } else snackbar.show("Gagal memuat file", "error");
    setLoading(false);
  };

  return {
    initPoolVendor,
    params,
    obsRef,
    tableRef,
    setParams,
    meta,
    setMeta,
    isLoading,
    setLoading,
    modalFormVendor,
    setModalFormVendor,
    editValues,
    setEditValues,
    files,
    setFiles,
    isViewFile,
    setViewFile,
    docUrl,
    setDocUrl,
    bankOpts,
    allDepartment,

    methods,
    isFetching,
    isFetchingNextPage,
    masterVendorList,
    fetchNextPage,
    hasNextPage,
    handleUpload,
    handlePost,
    handleDisplayDoc,
  };
};

export default useVendor;
